<template>
  <teleport to="body">
    <div
      v-if="openModal"
      class="modal"
      @click.self.prevent="
        openModal = !openModal;
        openNotesModal = false;
      ">
      <div class="product">
        <font-awesome-icon icon="xmark" class="exit" @click="openModal = !openModal" />

        <div v-if="futurePickup">
          <h2>Thanks {{ getWebOrder.order.name }}!</h2>
          <h3>A receipt and confirmation has been sent to {{ getWebOrder.order.email }}.</h3>
          <h3>Your order will be ready to collect at our {{ getWebOrder.order.location }} shop on {{ formattedDate }}<span v-if="getWebOrder.order.pickup_time"> at {{ getWebOrder.order.pickup_time }}</span>.</h3>
        </div>

        <div v-else-if="getWebOrder.order.delivery == 'deliver'">
          <h2>Thanks for your order, {{ getWebOrder.order.name }}!</h2>
          <h3>A receipt and confirmation has been sent to {{ getWebOrder.order.email }}.</h3>
          <div class="delivery-status">
            <h2>Order Status: <span v-if="orderComplete">Complete</span><span v-else>In Progress</span></h2>
            <!-- use the socket to update this -->
            <div class="status-bar">
              <div class="status-step" :class="{ active: getWebOrder.order.delivery_data.status === 'assigned' }">
              <div class="step-circle">1</div>
              <p>Confirmed</p>
              </div>
              <div class="status-connector" :class="{ active: ['preparing', 'out_for_delivery', 'delivered'].includes(getWebOrder.order.delivery_data.status) }"></div>
              <div class="status-step" :class="{ active: getWebOrder.order.delivery_data.status === 'preparing' }">
              <div class="step-circle">2</div>
              <p>Preparing</p>
              </div>
              <div class="status-connector" :class="{ active: ['out_for_delivery', 'delivered'].includes(getWebOrder.order.delivery_data.status) }"></div>
              <div class="status-step" :class="{ active: getWebOrder.order.delivery_data.status === 'out_for_delivery' }">
              <div class="step-circle">3</div>
              <p>Out for Delivery</p>
              </div>
              <div class="status-connector" :class="{ active: getWebOrder.order.delivery_data.status === 'delivered' }"></div>
              <div class="status-step" :class="{ active: getWebOrder.order.delivery_data.status === 'delivered' }">
              <div class="step-circle">4</div>
              <p>Delivered</p>
              </div>
            </div>
            <!-- map -->
            <div v-if="getWebOrder.order.delivery_data.status === 'out_for_delivery'">
              <h2>Your driver is on the way!</h2>
              <!-- <pre>{{ getWebOrder.order }} </pre> -->
              <p>Driver Name: {{ getWebOrder.order.delivery_data.driver }}</p>
              <!-- map component here with driver location -->
              <div class="map-section">
                <div id="map" ref="mapContainer" class="map"></div>
              </div>
            </div>
            <h3>Estimated time of delivery: {{ deliveryTime }}</h3>
            <h3>Delivery Address: {{ getWebOrder.order.delivery_data.address }}</h3>

            <div>
              <h2>Order Details</h2>
              <div v-for="product in getWebOrder.order.products" :key="product.hash" class="modifiers-layout">
                <h3>{{ product.name }} <font-awesome-icon v-if="product.complete" icon="fa-circle-check" /></h3>
                <p v-if="product.modifier_list.length != 0">Modifiers:</p>
                <p v-for="mod in product.modifier_list" :key="mod">{{ mod.name }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <h2>Thanks for your order, {{ getWebOrder.order.name }}!</h2>
          <h3>A receipt and confirmation has been sent to {{ getWebOrder.order.email }}.</h3>
          <h3>Your order will be ready for collection at our {{ getWebOrder.order.location }} shop in approximately 10 minutes.</h3>
          <h2>Order Status: <span v-if="orderComplete">Complete</span><span v-else>In Progress</span></h2>
          <div>
            <div>
              <ul class="modifiers">
                <div v-for="product in getWebOrder.order.products" :key="product.hash" class="modifiers-layout">
                  <h3>{{ product.name }} <font-awesome-icon v-if="product.complete" icon="fa-circle-check" /></h3>
                  <p v-if="product.modifier_list.length != 0">Modifiers:</p>
                  <p v-for="mod in product.modifier_list" :key="mod">{{ mod.name }}</p>
                  <p v-for="event in product.events" :key="event">&#9825; from {{ event.station }} @ {{ getLocalTime(event.time) }}</p>
                </div>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

export default {
  name: "WebOrderModal",
  props: ["open", "close"],
  data() {
    return {
      openModal: false,
      openNotesModal: false,
      orderStatus: "In Progress",

      map: null,
      markers: [],
      defaultCoords: { lat: -45.871478138862905, lng: 170.50666261494524 },
      driver: null,
    };
  },

  mounted() {},

  beforeUnmount() {
    this.cleanupMap();
  },

  methods: {
    ...mapActions(["updateWebOrder"]),

    getLocalTime(time) {
      const newTime = new Date(time);
      return newTime.getHours() + ":" + newTime.getMinutes();
    },

    initializeMap() {
      if (!this.$refs.mapContainer) return;
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.map = L.map(this.$refs.mapContainer).setView([this.defaultCoords.lat, this.defaultCoords.lng], 13);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
      }).addTo(this.map);
    },

    addMarker(lat, lng, iconOptions = {}) {
      if (!this.map) return;

      let marker = L.marker([lat, lng], iconOptions).addTo(this.map);

      this.markers.push(marker);
    },

    generateRandomHexColor() {
      let randomInt = Math.floor(Math.random() * 0xffffff);
      let hexString = randomInt.toString(16);
      let fullHexString = hexString.padStart(6, "0");
      return "#" + fullHexString;
    },

    addDriverMarker(driver, lat, lng) {
      let driverColor = this.generateRandomHexColor();

      let driverIcon = L.divIcon({
        className: "driver-marker",
        id: `driver-${driver}`, //should this be driver id instead of name?
        html: `<div class="driver-dot" style="background-color: ${driverColor};"></div>`,
      });

      this.clearMarkers();
      this.addMarker(lat, lng, { icon: driverIcon });
    },

    clearMarkers() {
      this.markers.forEach((marker) => {
        this.map.removeLayer(marker);
      });
      this.markers = [];
    },

    cleanupMap() {
      if (this.map) {
        this.clearMarkers();
        this.map.remove();
        this.map = null;
      }
    },
  },

  emits: ["showWebOrderModal"],

  computed: {
    ...mapGetters(["getWebOrder", "futurePickup", "getDriverPositions"]),

    orderComplete() {
      return this.getWebOrder.order.products.every((product) => product.complete);
    },

    formattedDate() {
      const date = new Date(this.getWebOrder.order.pickup_date);
      return date.toDateString();
    },

    pickupTime() {
      const start = Date.parse(this.getWebOrder.order.events.created_at);
      const approx = start + 600000;
      const mins = new Date(approx).getMinutes() < 10 ? "0" + new Date(approx).getMinutes() : new Date(approx).getMinutes();
      return new Date(approx).getHours() + ":" + mins;
    },

    deliveryTime(){
      const start = Date.parse(this.getWebOrder.order.events.created_at);
      // const approx = start + 600000;
      const approx = start + 1200000; // 20 mins atm 

      // calculate the delivery time 

      const mins = new Date(approx).getMinutes() < 10 ? "0" + new Date(approx).getMinutes() : new Date(approx).getMinutes();
      return new Date(approx).getHours() + ":" + mins;
    }
  },
  watch: {
    open() {
      this.openModal = true;
      if (localStorage.getItem("orderID") !== null && !("order" in this.getWebOrder)) {
        this.$parent.$parent.channel.push("order:status", localStorage.getItem("orderID")).receive("ok", async (reply) => {
          this.updateWebOrder({ order: reply.reply.rows[0].doc });
        });
      }
      if (this.getWebOrder.order.delivery_data.status === "out_for_delivery") {
        this.$nextTick(() => {
          this.initializeMap();
        });
        this.driver = this.getWebOrder.order.delivery_data.driver;
        this.addDriverMarker(this.driver, this.driver.latitude, this.driver.longitude);
      }
    },

    close() {
      this.openModal = false;
    },

    getDriverPositions: {  // there is an issue here? think getter is incorrect
      handler() {
          this.getDriverPositions.forEach((driver) => {
            if (driver.latitude && driver.longitude) {
              this.addDriverMarker(driver, driver.latitude, driver.longitude);
            }
          });
      },
      deep: true,
    },

    "getWebOrder.order.delivery_data.status"(newStatus) {
      if (newStatus === "out_for_delivery") {
        this.$nextTick(() => {
          this.initializeMap();
        });
        this.driver = this.getWebOrder.order.delivery_data.driver;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@monolith/styles";
.modal {
  .product {
    h3 {
      margin: 0.5rem;
    }
    .modifiers {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-evenly;
      align-items: flex-start;

      .modifiers-layout {
        flex: 0 0 25%;
        background-color: var(--primary-colour);
        padding: 1rem;
        border-radius: 0.25rem;
        margin: 1rem;
        @media only screen and (orientation: portrait) {
          flex: 0 0 75%;
        }
        p,
        h3 {
          margin: 0;
        }
        svg {
          font-size: 2rem;
          color: var(--action-colour);
          float: right;
        }
      }
    }

    .delivery-status {
      border-radius: 1rem;
      background-color: var(--primary-colour);
      padding: 1rem;
      margin: 1rem;
      h2 {
        color: var(--action-colour);
      }
      h3 {
        margin: 0.5rem 0;
      }

      .status-bar {
        display: flex;
        justify-content: space-between;
        margin: 1rem 0;

        .status-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          opacity: 0.5;

          &.active {
            opacity: 1;

            .step-circle {
              background-color: var(--action-colour);
              color: #fff;
            }
          }

          .step-circle {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background-color: var(--secondary-colour);
            color: var(--text-colour);
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 0.5rem;
          }
        }
      }

      .map-section {
        padding: 1rem;
        box-shadow: var(--box-shadow);
        background-color: var(--primary-colour);
        margin: 2rem;
        border-radius: 10px;
        position: relative;

        .map {
          width: 100%;
          height: 40rem;
          border-radius: 0.5rem;
          box-shadow: var(--box-shadow);
          background-color: var(--tertiary-colour);
          z-index: 10;

          :deep(.driver-marker) {
            .driver-dot {
              border-radius: 50% 50% 0 50%;
              transform: rotate(45deg);
              border: 1px solid var(--primary-colour);
              width: 15px;
              height: 15px;
              box-shadow: var(--box-shadow);
            }
          }
        }
      }
    }
  }
}
</style>
